import { PersonRole } from '@bp/planung-graphql-types';
import { ArrowRightIcon, Button, Table, TableColumns, TeacherIllustration } from '@bp/ui-components';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../../hooks/useAuthClaims';
import { useColumnsSort } from '../../../../hooks/useColumnsSort';
import { useLoadBasicData } from '../../../../hooks/useLoadBasicData';
import { useSelectedSchoolYear } from '../../../../hooks/useSelectedSchoolYear';
import { use_ContractsQuery, use_SubjectsQuery } from '../../../../types/planung-graphql-client-defs';
import { selectSchoolYearContract } from '../../../Persons/utils/contracts';
import { useDeputateLessons } from '../../hooks/useDeputateLessons';
import styles from '../../LessonsComponents.module.scss';

type OverbookedTeachersTableType = {
  uuid: string;
  teacherName: string;
  subjects: string;
  qualified: boolean;
  deputateRelation: string;
  relationRaw: number;
  status: string;
};

type OverbookedTeachersTableProps = {
  teacherUuid: string;
  qualifications: string[];
};

export const OverbookedTeachersTable = ({ teacherUuid, qualifications }: OverbookedTeachersTableProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const schoolYear = useSelectedSchoolYear();

  const { sorting, saveSorting } = useColumnsSort('status');

  const { teacherData } = useLoadBasicData({ pause: !schoolYear || !teacherUuid });

  const { lessons: allLessonData } = useDeputateLessons({
    organizationUuid: pimAuthClaims.getOrganizationUuid(),
    schoolYearUuid: schoolYear?.uuid ?? '',
  });

  const [{ data: contractsData }] = use_ContractsQuery({
    pause: !teacherData,
  });

  const [{ data: subjectsData }] = use_SubjectsQuery({
    pause: !teacherData,
  });

  const currentPerson = teacherData?.people.find((p) => p.uuid === teacherUuid);

  const currentContract = selectSchoolYearContract(
    contractsData?.contracts.filter((c) => c.personUuid === currentPerson?.uuid) ?? [],
    schoolYear,
  );

  const data: OverbookedTeachersTableType[] =
    teacherData?.people
      .flatMap((person) => {
        // deputate from lessons for person
        const plannedDeputate =
          allLessonData
            .map((lesson) => lesson.teachingLoadInfo?.deputatByTeacher?.get(person?.uuid) ?? 0)
            .reduce((acc, curr) => acc + curr, 0) ?? 0;

        // 0 when  no contract
        const contractDeputate = currentContract?.hoursWeekly ?? 0;

        const relation = contractDeputate ? (plannedDeputate / contractDeputate) * 100 : 0;

        const overbookedTeacher: OverbookedTeachersTableType = {
          uuid: person.uuid,
          teacherName: person.listName ?? '',
          deputateRelation: `${plannedDeputate.toFixed(2)}/${contractDeputate?.toFixed(2)}`,
          status: contractDeputate ? `${relation.toFixed(0)}%` : '-', // teachers without contract don't have a status
          relationRaw: relation,
          subjects:
            person?.qualifications
              ?.map((q) => subjectsData?.subjects.find((s) => s.uuid === q.subjectUuid)?.shortName.trim())
              .filter((s) => s)
              .sort()
              .join(', ') ?? '',
          qualified: person.qualifications.some((q) => qualifications?.includes(q.subjectUuid)),
        };
        return overbookedTeacher;
      })
      // only overbooked and qualified teachers
      .filter((d) => d.relationRaw > 100 && d.uuid !== currentPerson?.uuid && d.qualified) ?? [];

  const columns: TableColumns<OverbookedTeachersTableType>[] = [
    {
      header: t('persons.title', { count: 1 }),
      id: 'teacherName',
      accessorKey: 'teacherName',
      canExpand: true,
    },
    {
      header: t('subject.title', { count: 2 }),
      id: 'subjects',
      accessorKey: 'subjects',
      size: 200,
    },
    {
      header: t('deputate.title', { count: 1 }),
      id: 'deputateRelation',
      accessorKey: 'deputateRelation',
      size: 200,
    },

    {
      header: t('common.status'),
      id: 'status',
      accessorKey: 'status',
      size: 100,
    },
  ];

  return (
    <Table<OverbookedTeachersTableType>
      className={styles['overbook-teachers-table']}
      showSort
      sorting={sorting}
      onSortingChange={saveSorting}
      columns={columns}
      data={data}
      canScroll
      emptyStateSettings={{
        size: 'small',
        title: t('persons.noOverbooked'),
        subtitle: '',
        padding: 'l',
        icon: <TeacherIllustration />,
      }}
      maxHeight={276}
      customHeaderHeight='var(--spacing-7)'
      customRowHeight='var(--spacing-7)'
      lastColWidth='26px'
      showBorderRadius
      showShadow
      lastCol={() => <Button disabled hierarchy='ghost' icon={<ArrowRightIcon className='small' />} />}
    />
  );
};
